import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import BaseArticleView from "@ext/articleProvider/components/BaseArticleView";
import type { ProviderItemProps } from "@ext/articleProvider/models/types";
import t from "@ext/localization/locale/translate";
import FragmentUpdateService from "@ext/markdown/elements/fragment/edit/components/FragmentUpdateService";
import FragmentService from "@ext/markdown/elements/fragment/edit/components/Tab/FragmentService";
import { updateFragmentItem } from "@ext/markdown/elements/fragment/edit/logic/updateFragmentItem";
import { Placeholder } from "@ext/markdown/elements/placeholder/placeholder";
import NavigationEvents from "@ext/navigation/NavigationEvents";
import type { JSONContent } from "@tiptap/core";
import { useEffect } from "react";

const ArticleFragment = ({ item }: { item: ProviderItemProps }) => {
	const { fragments } = FragmentService.value;
	const apiUrlCreator = ApiUrlCreator.value;
	const router = useRouter();

	useEffect(() => {
		const listener = async ({ path, mutable }: { path: string; mutable: { preventGoto?: boolean } }) => {
			mutable.preventGoto = true;
			await FragmentUpdateService.updateContent(item.id, apiUrlCreator);
			FragmentService.closeItem();
			router.pushPath(path);
		};

		const clickToken = NavigationEvents.on("item-click", listener);
		const createToken = NavigationEvents.on("item-create", listener);
		const deleteToken = NavigationEvents.on("item-delete", listener);

		return () => {
			NavigationEvents.off(clickToken);
			NavigationEvents.off(createToken);
			NavigationEvents.off(deleteToken);
		};
	}, [item.id, router.pushPath]);

	const updateContent = (id: string, content: JSONContent, title: string) => {
		const fragment = fragments.get(id);
		if (!fragment) return;
		const updatedFragment = updateFragmentItem(fragment, content, title);

		FragmentService.setItems(
			Array.from(fragments.values()).map((item) => (item.id === updatedFragment.id ? updatedFragment : item)),
		);
	};

	const onCloseClick = async () => {
		await FragmentUpdateService.updateContent(item.id, apiUrlCreator);
		FragmentService.closeItem();
	};

	return (
		<ResourceService.Provider id={item.id} provider="fragment">
			<BaseArticleView
				extensions={[
					Placeholder.configure({
						placeholder: ({ editor, node }) => {
							if (
								editor.state.doc.firstChild.type.name === "paragraph" &&
								editor.state.doc.firstChild === node
							)
								return t("forms.fragment-editor.props.title.name");

							if (
								node.type.name === "paragraph" &&
								editor.state.doc.content.child(1) === node &&
								editor.state.doc.content.childCount === 2
							)
								return t("forms.fragment-editor.props.content.name");
						},
					}),
				]}
				extensionsOptions={{ isTemplateInstance: false, includeResources: true }}
				item={item}
				menuOptions={{ includeResources: true, fileName: item.id, isSmallEditor: true }}
				onCloseClick={onCloseClick}
				onUpdate={updateContent}
				providerType="fragment"
			/>
		</ResourceService.Provider>
	);
};

export default ArticleFragment;
